import type {
  BlueprintDocument,
  JsonValue,
  SecretDeclaration,
  SecretDriftKind,
  SecretKind,
  SecretMetadata,
} from '../api/types';

export const SECRET_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,62}$/;
export const RESERVED_SECRET_PREFIX = 'nebisys-';
export const MAX_SECRETS = 200;
/** Largest value the API accepts (`SECRET_VALUE_TOO_LARGE` above it). */
export const MAX_SECRET_VALUE_BYTES = 25 * 1024;
export const GENERATE_MIN_LENGTH = 16;
export const GENERATE_MAX_LENGTH = 128;

export type SecretNameProblem = 'invalid' | 'reserved' | 'taken';

/** Checks a new or renamed declaration name. Names must also differ ignoring case (Key Vault). */
export function secretNameProblem(
  name: string,
  existing: readonly string[],
  current?: string,
): SecretNameProblem | null {
  if (!SECRET_NAME_PATTERN.test(name)) return 'invalid';
  if (name.toLowerCase().startsWith(RESERVED_SECRET_PREFIX)) return 'reserved';
  const lower = name.toLowerCase();
  if (existing.some((other) => other !== current && other.toLowerCase() === lower)) return 'taken';
  return null;
}

// `$${` is an escape and is skipped; spaces inside the braces are allowed.
const SECRET_REFERENCE = /\$\$\{|\$\{(\s*)secret\.([A-Za-z0-9][A-Za-z0-9-]{0,62})(\s*)\}/g;

export interface SecretReferenceSite {
  resourceId: string;
  /** Path inside the resource's properties, for example ['settings', 'API_KEY']. */
  path: string[];
}

/** Every `${secret.<name>}` reference in resource properties, by secret name. */
export function secretReferences(doc: BlueprintDocument): Map<string, SecretReferenceSite[]> {
  const result = new Map<string, SecretReferenceSite[]>();
  const scan = (value: JsonValue | undefined, resourceId: string, path: string[]) => {
    if (typeof value === 'string') {
      for (const match of value.matchAll(SECRET_REFERENCE)) {
        const name = match[2];
        if (name === undefined) continue;
        const list = result.get(name) ?? [];
        list.push({ resourceId, path });
        result.set(name, list);
      }
    } else if (Array.isArray(value)) {
      value.forEach((item, i) => scan(item, resourceId, [...path, String(i)]));
    } else if (value && typeof value === 'object') {
      for (const [key, item] of Object.entries(value)) scan(item, resourceId, [...path, key]);
    }
  };
  for (const resource of doc.resources) scan(resource.properties, resource.id, []);
  return result;
}

function renameInValue(value: JsonValue, oldName: string, newName: string): JsonValue {
  if (typeof value === 'string') {
    return value.replace(SECRET_REFERENCE, (match, before: string | undefined, name, after) =>
      name === oldName ? '${' + (before ?? '') + 'secret.' + newName + (after ?? '') + '}' : match,
    );
  }
  if (Array.isArray(value)) return value.map((item) => renameInValue(item, oldName, newName));
  if (value && typeof value === 'object') {
    const result: { [key: string]: JsonValue } = {};
    for (const [key, item] of Object.entries(value)) result[key] = renameInValue(item, oldName, newName);
    return result;
  }
  return value;
}

/** Renames a declaration (keeping its position) and every `${secret.<old>}` reference in properties. */
export function renameSecret(doc: BlueprintDocument, oldName: string, newName: string): BlueprintDocument {
  if (oldName === newName) return doc;
  const secrets = Object.fromEntries(
    Object.entries(doc.secrets ?? {}).map(([key, value]) => [key === oldName ? newName : key, value]),
  );
  return {
    ...doc,
    secrets,
    resources: doc.resources.map((resource) =>
      resource.properties
        ? {
            ...resource,
            properties: renameInValue(resource.properties, oldName, newName) as typeof resource.properties,
          }
        : resource,
    ),
  };
}

/** Removes empty members of a declaration so saved documents stay minimal. */
export function tidyDeclaration(declaration: SecretDeclaration): SecretDeclaration {
  const result: SecretDeclaration = {};
  if (declaration.description) result.description = declaration.description;
  if (declaration.generate) result.generate = { length: declaration.generate.length };
  if (declaration.onChange) result.onChange = declaration.onChange;
  return result;
}

export function utf8Length(text: string): number {
  return new TextEncoder().encode(text).length;
}

export interface SecretFilter {
  kind: SecretKind | '';
  /** '' = all, 'any' = any drift, 'none' = no drift, or one drift kind. */
  drift: SecretDriftKind | 'any' | 'none' | '';
  resourceId: string;
}

export const NO_SECRET_FILTER: SecretFilter = { kind: '', drift: '', resourceId: '' };

export function filterSecrets(secrets: readonly SecretMetadata[], filter: SecretFilter): SecretMetadata[] {
  return secrets.filter((secret) => {
    if (filter.kind && secret.kind !== filter.kind) return false;
    if (filter.drift === 'any' && secret.drift.length === 0) return false;
    if (filter.drift === 'none' && secret.drift.length > 0) return false;
    if (
      filter.drift &&
      filter.drift !== 'any' &&
      filter.drift !== 'none' &&
      !secret.drift.includes(filter.drift)
    )
      return false;
    if (filter.resourceId && !secret.usedBy.some((u) => u.resourceId === filter.resourceId)) return false;
    return true;
  });
}

/** Resource IDs that use a secret (for highlighting on the canvas). */
export function resourcesUsing(secret: SecretMetadata | undefined): string[] {
  return [...new Set((secret?.usedBy ?? []).map((u) => u.resourceId))];
}
